import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  FileText, 
  Scale, 
  AlertTriangle, 
  UserCheck, 
  Shield, 
  Globe, 
  Mail,
  ExternalLink,
  BookOpen,
  Users,
  Ban,
  Eye,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';
import MultiplexAd from '../components/Ads/MultiplexAd';

const TermsOfService: React.FC = () => {
  const lastUpdated = "December 1, 2023";
  const effectiveDate = "December 1, 2023";
  
  // SEO Keywords
  const seoKeywords = [
    "AI tools directory terms",
    "artificial intelligence platform agreement",
    "TooliQ.ai terms of service",
    "AI tools legal agreement",
    "software terms and conditions",
    "tech platform user agreement",
    "digital service terms",
    "AI directory legal terms"
  ];

  // FAQ Schema Data
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What happens if I violate the Terms of Service?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We may terminate or suspend your access to the Service immediately, without prior notice, for conduct that violates our Terms or is harmful to other users, us, or third parties."
        }
      },
      {
        "@type": "Question",
        "name": "Is TooliQ.ai responsible for third-party AI tools listed?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "TooliQ.ai serves as an informational directory and does not endorse or guarantee the performance of listed AI tools. We are not responsible for the accuracy, reliability, or availability of third-party AI tools."
        }
      },
      {
        "@type": "Question",
        "name": "Can TooliQ.ai change these terms?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, we reserve the right to modify these terms at any time. We will provide notice of significant changes through our website. Continued use after changes constitutes acceptance of new terms."
        }
      },
      {
        "@type": "Question",
        "name": "What laws govern these terms?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "These Terms shall be governed by the laws of the State of Delaware. Any disputes shall be resolved in the courts of Delaware."
        }
      },
      {
        "@type": "Question",
        "name": "How can I contact legal support?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "For questions about these Terms, contact: legal@tooliq.ai. We typically respond to legal inquiries within 5 business days. Include 'Legal Inquiry' in your email subject line."
        }
      }
    ]
  };

  const sections = [
    {
      icon: <BookOpen className="w-5 h-5" />,
      title: "Acceptance of Terms",
      content: `By accessing and using TooliQ.ai ("the Service"), you accept and agree to be bound by the terms and provision of this agreement.`,
      keywords: ["user agreement", "terms acceptance", "legal binding"]
    },
    {
      icon: <UserCheck className="w-5 h-5" />,
      title: "User Responsibilities",
      content: `You are responsible for maintaining the confidentiality of your account and password and for restricting access to your computer. You agree to accept responsibility for all activities that occur under your account.`,
      keywords: ["account security", "user accountability", "password protection"]
    },
    {
      icon: <Globe className="w-5 h-5" />,
      title: "Service Description",
      content: `TooliQ.ai provides an AI tools directory service that helps users discover, compare, and access various artificial intelligence tools and applications. We serve as an informational platform and do not develop or own the tools listed.`,
      keywords: ["AI directory", "service description", "platform purpose"]
    },
    {
      icon: <Ban className="w-5 h-5" />,
      title: "Prohibited Uses",
      content: `You may not use the Service: for any illegal purpose; to solicit others to perform illegal acts; to violate international laws; to harass or harm others; to submit false information; to spam or phish; or to interfere with the Service's security features.`,
      keywords: ["restricted uses", "prohibited activities", "service limitations"]
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: "Intellectual Property",
      content: `The Service and its original content, features, and functionality are owned by TooliQ.ai and are protected by international copyright, trademark, and other intellectual property laws. AI tools listed are property of their respective owners.`,
      keywords: ["copyright", "trademark", "intellectual property rights"]
    },
    {
      icon: <AlertTriangle className="w-5 h-5" />,
      title: "Termination",
      content: `We may terminate or suspend your access to the Service immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.`,
      keywords: ["account termination", "service suspension", "violation consequences"]
    }
  ];

  const legalSections = [
    {
      title: "Limitation of Liability",
      points: [
        "TooliQ.ai serves as an informational directory and does not endorse or guarantee the performance of listed AI tools",
        "We are not responsible for the accuracy, reliability, or availability of third-party AI tools",
        "We shall not be liable for any indirect, incidental, special, consequential or punitive damages",
        "Total liability for any claim shall not exceed the amount paid to TooliQ.ai in the past six months"
      ]
    },
    {
      title: "Disclaimer of Warranties",
      points: [
        "The Service is provided 'as is' and 'as available' without warranties of any kind",
        "We do not warrant that the Service will be uninterrupted, secure, or error-free",
        "We do not warrant the accuracy or reliability of any AI tool information",
        "Your use of the Service is at your sole risk"
      ]
    },
    {
      title: "Third-Party Links & Tools",
      points: [
        "Our Service contains links to third-party AI tools and websites",
        "We have no control over and assume no responsibility for third-party content",
        "We do not endorse and are not responsible for any third-party terms or practices",
        "You access third-party tools at your own risk"
      ]
    },
    {
      title: "Governing Law",
      points: [
        "These Terms shall be governed by the laws of the State of Delaware",
        "Any disputes shall be resolved in the courts of Delaware",
        "We comply with applicable international laws and regulations",
        "The United Nations Convention on Contracts for the International Sale of Goods does not apply"
      ]
    },
    {
      title: "Changes to Terms",
      points: [
        "We reserve the right to modify these terms at any time",
        "We will provide notice of significant changes through our website",
        "Continued use after changes constitutes acceptance of new terms",
        "We will update the 'last updated' date when changes are made"
      ]
    },
    {
      title: "Contact Information",
      points: [
        "For questions about these Terms, contact: legal@tooliq.ai",
        "We typically respond to legal inquiries within 5 business days",
        "Formal legal notices should be sent via registered mail",
        "Include 'Legal Inquiry' in your email subject line"
      ]
    }
  ];

  // Quick Facts for Schema
  const quickFacts = [
    { icon: <Clock className="w-4 h-4" />, text: "Effective since December 2023" },
    { icon: <CheckCircle className="w-4 h-4" />, text: "Updated regularly" },
    { icon: <Shield className="w-4 h-4" />, text: "GDPR compliant" },
    { icon: <Globe className="w-4 h-4" />, text: "International coverage" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
      <Helmet>
        <title>Terms of Service | TooliQ.ai - AI Tools Directory</title>
        <meta 
          name="description" 
          content="Comprehensive Terms of Service for TooliQ.ai. Understand the legal agreement, user responsibilities, and terms governing your use of our AI tools directory platform. Updated December 2023." 
        />
        <meta 
          name="keywords" 
          content={seoKeywords.join(", ")} 
        />
        <link rel="canonical" href="https://tooliq.ai/terms-of-service" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Terms of Service | TooliQ.ai - AI Tools Directory" />
        <meta property="og:description" content="Legal terms and conditions for using TooliQ.ai's AI tools directory platform. User agreement, responsibilities, and service terms." />
        <meta property="og:url" content="https://tooliq.ai/terms-of-service" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://tooliq.ai/og-terms.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms of Service | TooliQ.ai" />
        <meta name="twitter:description" content="Understand the legal terms and conditions for using our AI tools directory platform." />
        <meta name="twitter:image" content="https://tooliq.ai/twitter-terms.jpg" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LegalService",
            "name": "TooliQ.ai Terms of Service",
            "description": "Legal terms and conditions for using the TooliQ.ai AI tools directory platform",
            "url": "https://tooliq.ai/terms-of-service",
            "datePublished": effectiveDate,
            "dateModified": lastUpdated,
            "provider": {
              "@type": "Organization",
              "name": "TooliQ.ai",
              "url": "https://tooliq.ai",
              "logo": "https://tooliq.ai/logo.png",
              "description": "AI Tools Directory Platform"
            },
            "keywords": seoKeywords.join(", "),
            "mainContentOfPage": {
              "@type": "WebPageElement",
              "cssSelector": ".legal-content"
            }
          })}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* Breadcrumb Navigation */}
      <nav className="bg-white/50 dark:bg-gray-800/50 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <a href="/" className="text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
                Home
              </a>
            </li>
            <li>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </li>
            <li className="text-slate-800 dark:text-slate-200 font-medium">
              Terms of Service
            </li>
          </ol>
        </div>
      </nav>

      {/* Header Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-slate-200 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-slate-800"></div>
        
        {/* Ad Slot 1: Top Banner */}
        <div className="max-w-6xl mx-auto px-6 pt-8">
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6 py-12 text-center">
          <div className="inline-flex items-center gap-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Scale className="w-4 h-4" />
            Legal Agreement
          </div>
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-slate-800 via-purple-600 to-blue-600 dark:from-slate-100 dark:via-purple-400 dark:to-blue-400 bg-clip-text text-transparent mb-6">
            Terms of Service
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Comprehensive legal agreement governing your use of TooliQ.ai's AI tools directory platform.
            Last updated: <span className="font-semibold">{lastUpdated}</span>
          </p>
          
          {/* Quick Facts */}
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            {quickFacts.map((fact, index) => (
              <div 
                key={index}
                className="flex items-center gap-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700"
              >
                <span className="text-purple-500">{fact.icon}</span>
                <span className="text-sm text-slate-700 dark:text-slate-300">{fact.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative max-w-6xl mx-auto px-6 pb-20 legal-content">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 p-6">
              <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-500" />
                Terms Sections
              </h3>
              <nav className="space-y-2">
                {sections.map((section, index) => (
                  <a
                    key={index}
                    href={`#section-${index}`}
                    className="block text-sm text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 py-2 px-3 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors flex items-center gap-2"
                  >
                    <ChevronRight className="w-3 h-3" />
                    {section.title}
                  </a>
                ))}
                {legalSections.map((section, index) => (
                  <a
                    key={`legal-${index}`}
                    href={`#legal-${index}`}
                    className="block text-sm text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 py-2 px-3 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors flex items-center gap-2"
                  >
                    <ChevronRight className="w-3 h-3" />
                    {section.title}
                  </a>
                ))}
              </nav>
              
              {/* Quick Links */}
              <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
                <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-3 text-sm">
                  Related Documents
                </h4>
                <div className="space-y-2">
                  <a
                    href="/privacy-policy"
                    className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                  >
                    <Shield className="w-3 h-3" />
                    Privacy Policy
                  </a>
                  <a
                    href="/cookie-policy"
                    className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                  >
                    <Eye className="w-3 h-3" />
                    Cookie Policy
                  </a>
                  <a
                    href="/acceptable-use-policy"
                    className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                  >
                    <Ban className="w-3 h-3" />
                    Acceptable Use Policy
                  </a>
                </div>
              </div>

              {/* Sidebar Ad */}
              <div className="mt-8">
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Important Notice with Schema */}
            <div 
              className="bg-yellow-50 dark:bg-yellow-900/20 rounded-2xl border border-yellow-200 dark:border-yellow-800 p-6"
              itemScope
              itemType="https://schema.org/Thing"
            >
              <meta itemProp="name" content="Important Legal Notice" />
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
                    Important Legal Notice
                  </h3>
                  <p 
                    className="text-yellow-700 dark:text-yellow-400 text-sm leading-relaxed"
                    itemProp="description"
                  >
                    These Terms of Service constitute a legally binding agreement between you and TooliQ.ai. 
                    By using our service, you acknowledge that you have read, understood, and agree to be bound 
                    by these terms. If you do not agree with any part of these terms, you must not use our service.
                  </p>
                </div>
              </div>
            </div>


            {/* Main Terms Sections */}
            <div className="space-y-8">
              {sections.map((section, index) => (
                <div 
                  key={index}
                  id={`section-${index}`}
                  className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-lg border border-slate-200 dark:border-slate-700 p-8 scroll-mt-20"
                  itemScope
                  itemType="https://schema.org/Article"
                >
                  <meta itemProp="keywords" content={section.keywords?.join(", ")} />
                  <meta itemProp="datePublished" content={effectiveDate} />
                  <meta itemProp="dateModified" content={lastUpdated} />
                  
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg text-purple-600 dark:text-purple-400">
                      {section.icon}
                    </div>
                    <h2 
                      className="text-2xl font-bold text-slate-800 dark:text-slate-200"
                      itemProp="headline"
                    >
                      {section.title}
                    </h2>
                  </div>
                  <p 
                    className="text-slate-700 dark:text-slate-300 leading-relaxed"
                    itemProp="articleBody"
                  >
                    {section.content}
                  </p>
                </div>
              ))}
            </div>


            {/* Legal Sections */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-200 mb-6 flex items-center gap-3">
                <Scale className="w-6 h-6 text-purple-500" />
                Legal Provisions & Limitations
              </h2>

              {legalSections.map((section, index) => (
                <div 
                  key={index}
                  id={`legal-${index}`}
                  className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-lg border border-slate-200 dark:border-slate-700 p-8 scroll-mt-20"
                >
                  <h3 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-4">
                    {section.title}
                  </h3>
                  <ul className="space-y-3">
                    {section.points.map((point, pointIndex) => (
                      <li key={pointIndex} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                        <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* User Agreement with Schema */}
            <div 
              className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-800 p-6"
              itemScope
              itemType="https://schema.org/Question"
            >
              <meta itemProp="name" content="User Agreement Terms" />
              <h3 className="font-bold text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2">
                <UserCheck className="w-4 h-4" />
                User Agreement
              </h3>
              <p className="text-blue-700 dark:text-blue-400 text-sm leading-relaxed mb-4">
                By using TooliQ.ai, you confirm that:
              </p>
              <ul 
                className="text-blue-700 dark:text-blue-400 text-sm space-y-2"
                itemProp="acceptedAnswer"
                itemScope
                itemType="https://schema.org/Answer"
              >
                <meta itemProp="text" content="User must be at least 13 years old, have legal capacity, comply with laws, and not use service for unauthorized purposes." />
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  You are at least 13 years of age (or 16 in the European Union)
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  You have the legal capacity to enter into this agreement
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  You will comply with all applicable laws and regulations
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  You will not use the Service for any unauthorized or illegal purpose
                </li>
              </ul>
            </div>

            {/* FAQ Section for SEO */}
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-lg border border-slate-200 dark:border-slate-700 p-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6 flex items-center gap-3">
                <Info className="w-6 h-6 text-purple-500" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqSchema.mainEntity.map((faq: any, index: number) => (
                  <div key={index} className="border border-slate-200 dark:border-slate-700 rounded-xl p-5">
                    <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">
                      {faq.name}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">
                      {faq.acceptedAnswer.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact & Support */}
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-lg border border-slate-200 dark:border-slate-700 p-8 text-center">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-4">
                Questions About Our Terms?
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-2xl mx-auto">
                If you have any questions about these Terms of Service or need clarification on any provision, 
                please contact our legal team. We're here to help you understand our terms and your responsibilities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="mailto:legal@tooliq.ai"
                  className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Email Legal Team
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 font-semibold py-3 px-6 rounded-xl transition-colors"
                >
                  <Users className="w-4 h-4" />
                  Contact Support
                </a>
              </div>
            </div>

            {/* Footer Notice */}
            <div className="text-center text-sm text-slate-500 dark:text-slate-400 pt-8 border-t border-slate-200 dark:border-slate-700">
              <p>
                © {new Date().getFullYear()} TooliQ.ai. All rights reserved. These Terms of Service were 
                last updated on {lastUpdated} and are effective as of {effectiveDate}.
              </p>
              <div className="mt-2 text-xs text-slate-400">
                Version 2.1 | Document ID: TOS-2023-12-01
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default TermsOfService;